import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { KeyRound, MailCheck } from 'lucide-react';
import { useForgotPasswordMutation } from '../../store/api/authApi';
import Turnstile, { isTurnstileConfigured } from '../common/Turnstile';
import AuthShell, { AuthField, AuthSubmit, AuthAlert } from './AuthShell';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef(null);

  // The Turnstile token is single use, so drop it and re-arm the widget
  // whenever a submit does not go through.
  const resetCaptcha = () => {
    setCaptchaToken('');
    turnstileRef.current?.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    if (isTurnstileConfigured() && !captchaToken) {
      setError('Please complete the security check');
      return;
    }

    try {
      await forgotPassword({ email, turnstileToken: captchaToken }).unwrap();
      setSuccess(true);
    } catch (err) {
      setError(err.data?.message || 'Failed to send reset email. Please try again.');
      resetCaptcha();
    }
  };

  if (success) {
    return (
      <AuthShell
        width="narrow"
        icon={<MailCheck size={20} />}
        eyebrow="Step 1 of 2"
        title="Check your email"
        subtitle={
          <>
            If an account exists for <strong>{email}</strong>, we have sent it a
            password reset code.
          </>
        }
      >
        <button
          type="button"
          className="mp-auth-btn is-primary is-block"
          onClick={() => navigate('/reset-password', { state: { email } })}
        >
          Enter reset code
        </button>

        <p className="mp-auth-foot">
          <Link to="/login">Back to sign in</Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      width="narrow"
      icon={<KeyRound size={20} />}
      eyebrow="Password reset"
      title="Forgot your password?"
      subtitle="Enter the email on your account and we will send you a reset code."
    >
      {/* {error && <AuthAlert>{error}</AuthAlert>} */}

      <form onSubmit={handleSubmit} noValidate>
        <div className="mp-auth-fields">
          <AuthField
            id="email"
            type="email"
            label="Work email"
            placeholder="you@company.com"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error && error.toLowerCase().includes('email') ? error : undefined}
          />
        </div>

        <Turnstile
          ref={turnstileRef}
          action="forgot-password"
          onVerify={(token) => {
            setCaptchaToken(token);
            setError('');
          }}
          onExpire={() => setCaptchaToken('')}
          onError={() => setCaptchaToken('')}
        />

        <div style={{ marginTop: 22 }}>
          <AuthSubmit label="Send reset code" loading={isLoading} />
        </div>
      </form>

      <p className="mp-auth-foot">
        Remember your password? <Link to="/login">Sign in</Link>
      </p>
    </AuthShell>
  );
};

export default ForgotPassword;
